import React, { forwardRef } from "react";
import { Box, type BoxProps } from "@mui/material";
import { tokens } from "@/theme/tokens";
import { cssTransition } from "@/components/Chat/motion";

export interface SoftProps extends BoxProps {
  /** `raised` (default): a resting card, tonally distinct from the page (its
   *  own surface colour + a hairline ring + a flat drop shadow) — the single
   *  card pattern for every workspace list.
   *  `sm`: the same treatment, tighter radius, for compact inline cards.
   *  `inset`: sits flush with the page (no shadow) for content nested INSIDE
   *  another card (a code block, a cropped image) — still gets the ring, so
   *  it never disappears against a background that happens to match. */
  variant?: "raised" | "inset" | "sm";
  /** Hover lift + press + focus ring, for clickable cards/rows. */
  interactive?: boolean;
}

/**
 * The resting-surface primitive for content that sits ON the solid page
 * (cards, panels, controls). Flat by design: a real background tone (not the
 * page colour), a 1px ring that stays visible in both themes — dark mode's
 * chrome hairlines are intentionally transparent (nav rows separate by tone
 * alone), but a standalone card still needs an edge you can always see — and
 * one shadow step from the shared elevation ramp. No soft-UI extrusion.
 */
export const Soft = forwardRef<HTMLDivElement, SoftProps>(function Soft(
  { variant = "raised", interactive = false, sx, children, ...rest },
  ref,
) {
  return (
    <Box
      ref={ref}
      sx={[
        (t) => {
          const dark = t.palette.mode === "dark";
          const ring = dark ? "rgba(255,255,255,0.09)" : "rgba(16,24,40,0.08)";
          const isInset = variant === "inset";
          return {
            position: "relative",
            backgroundColor: isInset ? "background.default" : "background.paper",
            border: `1px solid ${ring}`,
            borderRadius: `${variant === "sm" ? tokens.radius.sm : tokens.radius.md}px`,
            boxShadow: isInset ? "none" : tokens.elevation[1],
            transition: "box-shadow 160ms ease, border-color 160ms ease",
            ...(interactive && {
              cursor: "pointer",
              transition: cssTransition.lift,
              "&:hover": { boxShadow: tokens.elevation[2], transform: "translateY(-1px)" },
              "&:active": { boxShadow: tokens.elevation[1], transform: "translateY(0)" },
              "&:focus-visible": {
                outline: "none",
                boxShadow: dark ? tokens.focusRing.dark : tokens.focusRing.light,
              },
              "@media (prefers-reduced-motion: reduce)": {
                transition: "box-shadow 200ms ease",
                "&:hover": { transform: "none" },
              },
            }),
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </Box>
  );
});
